import { Plus, Search, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BadgeReste } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/field';
import { ChargementBloc, EtatErreur, EtatVide } from '@/components/ui/feedback';
import { Pagination } from '@/components/ui/pagination';
import { TBody, TD, TH, THTri, THead, TR, Table } from '@/components/ui/table';
import { PageBody, PageHeader } from '@/layouts/AppShell';
import { useAntiRebond } from '@/lib/anti-rebond';
import { date, dh, entier, telephone } from '@/lib/format';
import { useClients } from './api/clients.api';
import { NouveauClientDialog } from './components/NouveauClientDialog';

type Tri = 'nom' | 'dernier_achat' | 'total_depense' | 'reste_du';

export function ClientsPage() {
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [tri, setTri] = useState<Tri>('dernier_achat');
  const [sens, setSens] = useState<'asc' | 'desc'>('desc');
  const [nouveau, setNouveau] = useState(false);

  const naviguer = useNavigate();
  /* Même raison que sur la page Commandes : une frappe déclenchait la liste et
   * son comptage, soit un balayage complet de l'annuaire à chaque lettre. */
  const rechercheDifferee = useAntiRebond(recherche);

  const { data, isPending, error, refetch, isFetching } = useClients({
    page,
    limit: 25,
    q: rechercheDifferee || undefined,
    tri,
    sens,
  });

  function trierPar(colonne: Tri) {
    if (colonne === tri) setSens((s) => (s === 'asc' ? 'desc' : 'asc'));
    else {
      setTri(colonne);
      setSens(colonne === 'nom' ? 'asc' : 'desc');
    }
    setPage(1);
  }

  return (
    <>
      <PageHeader
        titre="Clients"
        description="Annuaire et historique d'achats."
        actions={
          <Button
            variant="primary"
            icone={<Plus aria-hidden className="h-4 w-4" />}
            onClick={() => setNouveau(true)}
          >
            Nouveau client
          </Button>
        }
      />

      <PageBody>
        <Card>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="relative min-w-0 flex-1 sm:max-w-sm">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
              />
              <Input
                value={recherche}
                onChange={(e) => {
                  setRecherche(e.target.value);
                  setPage(1);
                }}
                placeholder="Nom ou téléphone…"
                className="pl-8"
                aria-label="Rechercher un client"
              />
            </div>
            {isFetching ? <span className="text-xs text-ink-muted">Actualisation…</span> : null}
          </div>

          {isPending ? (
            <ChargementBloc />
          ) : error ? (
            <EtatErreur erreur={error} onReessayer={() => void refetch()} />
          ) : data.data.length === 0 ? (
            <EtatVide
              titre="Aucun client"
              description={
                recherche
                  ? 'Aucun client ne correspond à cette recherche.'
                  : 'Les clients se créent automatiquement à la première commande.'
              }
              icone={<Users aria-hidden className="h-6 w-6" />}
            />
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <THTri colonne="nom" triActuel={tri} sens={sens} onTrier={trierPar}>
                      Client
                    </THTri>
                    <TH>Téléphone</TH>
                    <TH numerique>Achats</TH>
                    <THTri
                      colonne="total_depense"
                      triActuel={tri}
                      sens={sens}
                      onTrier={trierPar}
                      numerique
                    >
                      Total dépensé
                    </THTri>
                    <THTri colonne="reste_du" triActuel={tri} sens={sens} onTrier={trierPar}>
                      Reste dû
                    </THTri>
                    <THTri colonne="dernier_achat" triActuel={tri} sens={sens} onTrier={trierPar}>
                      Dernier achat
                    </THTri>
                  </TR>
                </THead>
                <TBody>
                  {data.data.map((c) => (
                    <TR key={c.id} onClick={() => naviguer(`/clients/${c.id}`)}>
                      <TD className="font-medium">
                        <Link
                          to={`/clients/${c.id}`}
                          className="hover:text-mustard-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {c.nom}
                        </Link>
                      </TD>
                      <TD className="whitespace-nowrap text-ink-muted">{telephone(c.tel)}</TD>
                      <TD numerique>{entier(c.nombre_achats)}</TD>
                      <TD numerique className="font-semibold">
                        {dh(c.total_depense)}
                      </TD>
                      <TD>
                        <BadgeReste reste={c.reste_du} />
                      </TD>
                      <TD className="whitespace-nowrap text-ink-muted">
                        {c.dernier_achat ? date(c.dernier_achat) : '—'}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              <Pagination meta={data.meta} onPage={setPage} libelle="clients" />
            </>
          )}
        </Card>
      </PageBody>

      <NouveauClientDialog
        ouvert={nouveau}
        onFermer={() => setNouveau(false)}
        onCree={(client) => naviguer(`/clients/${client.id}`)}
      />
    </>
  );
}

